import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  DropdownButton, Dropdown, InputGroup, Tab, Nav, Tabs
} from 'react-bootstrap';
import axios from 'axios';
import { 
  FaPhotoVideo, FaImage, FaVideo, FaFilter, FaSearch, 
  FaEdit, FaTrash, FaEye, FaEyeSlash, FaPlus,
  FaUpload, FaYoutube, FaVimeo, FaCalendar,
  FaGraduationCap, FaBook, FaFlask, FaFutbol,
  FaMusic, FaUsers, FaTrophy, FaUniversity,
  FaChalkboardTeacher, FaBuilding, FaInfoCircle,
  FaSort, FaCheck, FaTimes
} from 'react-icons/fa';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const GalleryAdmin = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Category configuration
  const categoryConfig = {
    'CAMPUS': { 
      icon: <FaUniversity />, 
      name: 'Campus', 
      color: '#4CAF50',
      bgColor: '#E8F5E9'
    },
    'CLASSROOM': { 
      icon: <FaChalkboardTeacher />, 
      name: 'Classroom', 
      color: '#2196F3',
      bgColor: '#E3F2FD'
    },
    'LABORATORY': { 
      icon: <FaFlask />, 
      name: 'Laboratory', 
      color: '#9C27B0',
      bgColor: '#F3E5F5'
    },
    'LIBRARY': { 
      icon: <FaBook />, 
      name: 'Library', 
      color: '#795548',
      bgColor: '#EFEBE9'
    },
    'SPORTS': { 
      icon: <FaFutbol />, 
      name: 'Sports', 
      color: '#FF5722',
      bgColor: '#FBE9E7'
    },
    'CULTURAL_EVENT': { 
      icon: <FaMusic />, 
      name: 'Cultural Events', 
      color: '#E91E63',
      bgColor: '#FCE4EC'
    },
    'ACADEMIC_EVENT': { 
      icon: <FaGraduationCap />, 
      name: 'Academic Events', 
      color: '#3F51B5',
      bgColor: '#E8EAF6'
    },
    'ANNUAL_FUNCTION': { 
      icon: <FaCalendar />, 
      name: 'Annual Function', 
      color: '#FF9800',
      bgColor: '#FFF3E0'
    },
    'WORKSHOP_SEMINAR': { 
      icon: <FaUsers />, 
      name: 'Workshops', 
      color: '#009688',
      bgColor: '#E0F2F1'
    },
    'EXAMINATION': { 
      icon: <FaBook />, 
      name: 'Examinations', 
      color: '#607D8B',
      bgColor: '#ECEFF1'
    },
    'GRADUATION': { 
      icon: <FaGraduationCap />, 
      name: 'Graduation', 
      color: '#673AB7',
      bgColor: '#EDE7F6'
    },
    'FESTIVAL': { 
      icon: <FaCalendar />, 
      name: 'Festivals', 
      color: '#FF4081',
      bgColor: '#FCE4EC'
    },
    'STUDENT_ACTIVITY': { 
      icon: <FaUsers />, 
      name: 'Student Activities', 
      color: '#00BCD4',
      bgColor: '#E0F7FA'
    },
    'FACULTY': { 
      icon: <FaChalkboardTeacher />, 
      name: 'Faculty', 
      color: '#8BC34A',
      bgColor: '#F1F8E9'
    },
    'INFRASTRUCTURE': { 
      icon: <FaBuilding />, 
      name: 'Infrastructure', 
      color: '#9E9E9E',
      bgColor: '#FAFAFA'
    },
    'ACHIEVEMENTS': { 
      icon: <FaTrophy />, 
      name: 'Achievements', 
      color: '#FFC107',
      bgColor: '#FFFDE7'
    },
    'ADMISSIONS': { 
      icon: <FaUniversity />, 
      name: 'Admissions', 
      color: '#795548',
      bgColor: '#EFEBE9'
    },
    'ORIENTATION': { 
      icon: <FaUsers />, 
      name: 'Orientation', 
      color: '#3F51B5',
      bgColor: '#E8EAF6'
    },
    'ALUMNI_MEET': { 
      icon: <FaUsers />, 
      name: 'Alumni Meet', 
      color: '#607D8B',
      bgColor: '#ECEFF1'
    },
    'OTHER': { 
      icon: <FaInfoCircle />, 
      name: 'Other', 
      color: '#9E9E9E',
      bgColor: '#F5F5F5'
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    gallery_type: 'photo',
    category: 'CAMPUS',
    image_path: '',
    video_url: '',
    is_active: true
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchGallery();
  }, [selectedCategory, selectedType]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      gallery_type: 'photo',
      category: 'CAMPUS',
      image_path: '',
      video_url: '',
      is_active: true
    });
    setImagePreview(null);
    setEditingItem(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchGallery = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      let url = `${API_BASE_URL}/gallery.php?admin=true`;
      
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      
      if (selectedType !== 'all') {
        url += `&type=${selectedType}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedItems = response.data.data.map(item => ({
          ...item,
          id: parseInt(item.id),
          is_active: item.is_active === "1" || item.is_active === 1
        }));
        
        setGalleryItems(processedItems);
      } else {
        setError('Failed to load gallery items');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGalleryTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      gallery_type: type,
      image_path: type === 'photo' ? prev.image_path : '',
      video_url: type === 'video' ? prev.video_url : ''
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Set file for upload
      setFormData(prev => ({
        ...prev,
        image_file: file,
        gallery_type: 'photo',
        video_url: '' // Clear video URL when image is selected
      }));
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      image_path: '',
      image_file: null
    }));
  };

  const uploadImage = async (file) => {
    try {
      setUploadingImage(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_gallery_image.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.image_url;
      }
      return null;
    } catch (err) {
      console.error('Image upload error:', err);
      setError('Failed to upload image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleVideoUrlChange = (url) => {
    let processedUrl = url;
    
    // Convert YouTube watch URL to embed URL
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1];
      const ampersandPosition = videoId.indexOf('&');
      if (ampersandPosition !== -1) {
        processedUrl = `https://www.youtube.com/embed/${videoId.substring(0, ampersandPosition)}`;
      } else {
        processedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
    }
    // Convert youtu.be URL to embed URL
    else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1];
      processedUrl = `https://www.youtube.com/embed/${videoId}`;
    }
    // Convert Vimeo URL to embed URL
    else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1];
      processedUrl = `https://player.vimeo.com/video/${videoId}`;
    }
    
    setFormData(prev => ({
      ...prev,
      video_url: processedUrl,
      gallery_type: 'video',
      image_path: '' // Clear image path when video is selected
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      
      let dataToSend = { ...formData };
      
      // Remove image_file from dataToSend if it exists
      if (dataToSend.image_file) {
        delete dataToSend.image_file;
      }
      
      // If there's a new image to upload
      if (formData.image_file) {
        const uploadedUrl = await uploadImage(formData.image_file);
        if (uploadedUrl) {
          dataToSend.image_path = uploadedUrl;
        } else {
          setError('Failed to upload image');
          return;
        }
      }
      
      // Ensure boolean values are converted to 1/0
      dataToSend.is_active = dataToSend.is_active ? 1 : 0;
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingItem) {
        response = await axios.put(
          `${API_BASE_URL}/gallery.php?id=${editingItem.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/gallery.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingItem ? 'Gallery item updated successfully!' : 'Gallery item added successfully!');
        setShowModal(false);
        resetForm();
        fetchGallery();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error saving gallery item');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery item? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/gallery.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Gallery item deleted successfully!');
          fetchGallery();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(response.data.message || 'Failed to delete gallery item');
        }
      } catch (err) {
        setError('Error deleting gallery item');
        console.error(err);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      gallery_type: item.gallery_type,
      category: item.category,
      image_path: item.image_path || '',
      video_url: item.video_url || '',
      is_active: item.is_active === true || item.is_active === "1" || item.is_active === 1
    });
    
    // Set image preview if image_path exists
    if (item.image_path) {
      setImagePreview(getImageUrl(item.image_path));
    }
    
    setShowModal(true);
  };

  const handleToggleActive = async (item) => {
    try {
      const currentActive = item.is_active === true || item.is_active === "1" || item.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        title: item.title,
        description: item.description || '',
        gallery_type: item.gallery_type,
        category: item.category,
        image_path: item.image_path || '',
        video_url: item.video_url || '',
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/gallery.php?id=${item.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchGallery();
      } else {
        setError('Failed to update status');
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const getYoutubeThumbnail = (url) => {
    if (!url) return null;
    const videoId = url.includes('youtube.com/embed/') 
      ? url.split('youtube.com/embed/')[1]
      : url.includes('youtu.be/') 
        ? url.split('youtu.be/')[1]
        : null;
    
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/0.jpg`;
    }
    return null;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredItems = galleryItems.filter(item => {
    const matchesSearch = searchTerm === '' || 
                         item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  // Admin CSS Styles
  const styles = `
  .admin-gallery-container {
    background: #f8f9fa;
    min-height: 100vh;
  }

  .admin-header {
    background: white;
    padding: 1.5rem 0;
    border-bottom: 1px solid #e9ecef;
    margin-bottom: 2rem;
  }

  .admin-header h2 {
    color: #2c3e50;
    font-weight: 600;
    margin: 0;
  }

  .admin-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    transition: all 0.3s ease;
  }

  .admin-card:hover {
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }

  .gallery-card-admin {
    border: 1px solid #e9ecef;
    border-radius: 10px;
    overflow: hidden;
    transition: all 0.3s ease;
    background: white;
  }

  .gallery-card-admin:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 20px rgba(0,0,0,0.1);
    border-color: #4a90e2;
  }

  .card-image-container {
    height: 200px;
    overflow: hidden;
    position: relative;
    background: #f8f9fa;
  }

  .card-image-container img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .gallery-card-admin:hover .card-image-container img {
    transform: scale(1.05);
  }

  .category-badge-admin {
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 5px;
    backdrop-filter: blur(5px);
    border: 1px solid rgba(255,255,255,0.2);
  }

  .status-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
  }

  .status-badge.active {
    background: rgba(76, 175, 80, 0.2);
    color: #4CAF50;
  }

  .status-badge.inactive {
    background: rgba(244, 67, 54, 0.2);
    color: #f44336;
  }

  .video-indicator {
    position: absolute;
    bottom: 10px;
    right: 10px;
    background: rgba(0,0,0,0.7);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .card-content-admin {
    padding: 15px;
  }

  .card-title-admin {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-description-admin {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 10px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
  }

  .card-footer-admin {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 10px;
    border-top: 1px solid #f1f1f1;
  }

  .card-date {
    font-size: 0.8rem;
    color: #95a5a6;
  }

  .action-buttons {
    display: flex;
    gap: 5px;
  }

  .action-btn {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    transition: all 0.2s ease;
  }

  .action-btn:hover {
    background: #f8f9fa;
    color: #4a90e2;
    border-color: #4a90e2;
  }

  .action-btn.delete:hover {
    color: #f44336;
    border-color: #f44336;
  }

  /* Filter Section */
  .filter-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 25px;
    border: 1px solid #e9ecef;
  }

  .search-input-admin {
    border-radius: 8px;
    border: 1px solid #e9ecef;
    padding: 10px 15px;
    transition: all 0.3s ease;
  }

  .search-input-admin:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }

  /* Modal Styles */
  .gallery-modal-admin .modal-content {
    border-radius: 15px;
    border: none;
    box-shadow: 0 10px 40px rgba(0,0,0,0.15);
  }

  .modal-header-admin {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 15px 15px 0 0;
    padding: 1.5rem;
  }

  .modal-header-admin .modal-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
  }

  .preview-container {
    border: 2px dashed #e9ecef;
    border-radius: 10px;
    padding: 20px;
    text-align: center;
    background: #f8f9fa;
    min-height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
  }

  .preview-container.has-image {
    border-style: solid;
    border-color: #4a90e2;
    background: white;
  }

  .preview-image {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    margin-bottom: 15px;
  }

  .video-preview-frame {
    width: 100%;
    height: 300px;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  /* Stats Cards */
  .stats-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e9ecef;
    text-align: center;
    transition: all 0.3s ease;
  }

  .stats-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  }

  .stats-card h3 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 5px;
    color: #2c3e50;
  }

  .stats-card p {
    color: #6c757d;
    margin: 0;
    font-size: 0.9rem;
  }

  .stats-card.photo {
    border-top: 4px solid #4CAF50;
  }

  .stats-card.video {
    border-top: 4px solid #f44336;
  }

  .stats-card.total {
    border-top: 4px solid #4a90e2;
  }

  /* Empty State */
  .empty-state-admin {
    text-align: center;
    padding: 60px 20px;
    background: white;
    border-radius: 12px;
    border: 2px dashed #e9ecef;
  }

  .empty-state-admin .icon {
    font-size: 3rem;
    color: #bdc3c7;
    margin-bottom: 20px;
  }

  /* Loading State */
  .loading-state {
    text-align: center;
    padding: 60px 20px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #4a90e2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Responsive */
  @media (max-width: 768px) {
    .admin-header {
      padding: 1rem 0;
    }
    
    .filter-section {
      padding: 15px;
    }
    
    .stats-card {
      margin-bottom: 15px;
    }
    
    .action-buttons {
      flex-wrap: wrap;
    }
    
    .gallery-modal-admin .modal-dialog {
      margin: 10px;
    }
  }

  /* Custom Form Controls */
  .form-control-custom {
    border-radius: 8px;
    border: 1px solid #e9ecef;
    padding: 10px 15px;
    transition: all 0.3s ease;
  }

  .form-control-custom:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }

  .btn-type-selector {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
  }

  .btn-type-selector .btn {
    flex: 1;
    padding: 12px;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.3s ease;
  }

  .btn-type-selector .btn.active {
    background: #4a90e2;
    color: white;
    border-color: #4a90e2;
  }

  .category-select {
    border-radius: 8px;
    border: 1px solid #e9ecef;
    padding: 10px 15px;
    width: 100%;
    background: white;
  }

  .category-option {
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .category-option-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
  }
  `;

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <style>{styles}</style>
        <div className="admin-gallery-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading gallery items...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <style>{styles}</style>
      <div className="admin-gallery-container">
        {/* Header */}
        <div className="admin-header">
          <Container>
            <Row className="align-items-center">
              <Col>
                <h2>Gallery Management</h2>
                <p className="text-muted mb-0">Manage photos and videos in your gallery</p>
              </Col>
              <Col className="text-end">
                <Button 
                  variant="primary" 
                  onClick={() => setShowModal(true)}
                  className="d-inline-flex align-items-center"
                >
                  <FaPlus className="me-2" /> Add New Item
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container>
          {/* Stats */}
          <Row className="mb-4">
            <Col md={4} className="mb-3">
              <div className="stats-card photo">
                <h3>{galleryItems.filter(i => i.gallery_type === 'photo').length}</h3>
                <p>Photos</p>
              </div>
            </Col>
            <Col md={4} className="mb-3">
              <div className="stats-card video">
                <h3>{galleryItems.filter(i => i.gallery_type === 'video').length}</h3>
                <p>Videos</p>
              </div>
            </Col>
            <Col md={4} className="mb-3">
              <div className="stats-card total">
                <h3>{galleryItems.length}</h3>
                <p>Total Items</p>
              </div>
            </Col>
          </Row>

          {/* Messages */}
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-4">
              <FaTimes className="me-2" /> {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" onClose={() => setSuccess('')} dismissible className="mb-4">
              <FaCheck className="me-2" /> {success}
            </Alert>
          )}

          {/* Filters */}
          <div className="filter-section mb-4">
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label><FaFilter className="me-2" /> Category</Form.Label>
                  <Form.Select 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="form-control-custom"
                  >
                    <option value="all">All Categories</option>
                    {Object.entries(categoryConfig).map(([key, category]) => (
                      <option key={key} value={key}>
                        <span style={{ color: category.color, marginRight: '8px' }}>
                          {React.cloneElement(category.icon, { style: { marginRight: '8px' } })}
                        </span>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Media Type</Form.Label>
                  <Form.Select 
                    value={selectedType} 
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="form-control-custom"
                  >
                    <option value="all">All Types</option>
                    <option value="photo">Photos Only</option>
                    <option value="video">Videos Only</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label><FaSearch className="me-2" /> Search</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search by title or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input-admin"
                    />
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setSearchTerm('')}
                      disabled={!searchTerm}
                    >
                      Clear
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Gallery Items */}
          {filteredItems.length === 0 ? (
            <div className="empty-state-admin">
              <div className="icon">
                <FaPhotoVideo />
              </div>
              <h4>No Gallery Items Found</h4>
              <p className="text-muted mb-4">
                {searchTerm || selectedCategory !== 'all' || selectedType !== 'all' 
                  ? 'Try adjusting your filters or search terms'
                  : 'Get started by adding your first gallery item'}
              </p>
              <Button variant="primary" onClick={() => setShowModal(true)}>
                <FaPlus className="me-2" /> Add Gallery Item
              </Button>
            </div>
          ) : (
            <Row>
              {filteredItems.map((item) => (
                <Col key={item.id} xl={3} lg={4} md={6} className="mb-4">
                  <div className="gallery-card-admin">
                    <div 
                      className="card-image-container"
                      onClick={() => handleEdit(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      {item.gallery_type === 'photo' ? (
                        <img
                          src={getImageUrl(item.image_path)}
                          alt={item.title}
                        />
                      ) : (
                        <div 
                          style={{ 
                            width: '100%',
                            height: '100%',
                            background: `url(${getYoutubeThumbnail(item.video_url)}) center/cover no-repeat`
                          }}
                        >
                          <div className="video-indicator">
                            <FaVideo size={12} /> Video
                          </div>
                        </div>
                      )}
                      
                      <div 
                        className="category-badge-admin"
                        style={{ 
                          backgroundColor: categoryConfig[item.category]?.bgColor,
                          color: categoryConfig[item.category]?.color
                        }}
                      >
                        {categoryConfig[item.category]?.icon}
                        <span>{categoryConfig[item.category]?.name}</span>
                      </div>
                      
                      <div className={`status-badge ${item.is_active ? 'active' : 'inactive'}`}>
                        {item.is_active ? <FaEye /> : <FaEyeSlash />}
                      </div>
                    </div>
                    
                    <div className="card-content-admin">
                      <h6 className="card-title-admin">{item.title}</h6>
                      <p className="card-description-admin">
                        {item.description || 'No description'}
                      </p>
                      
                      <div className="card-footer-admin">
                        <div className="card-date">
                          {formatDate(item.created_at)}
                        </div>
                        <div className="action-buttons">
                          <button
                            className={`action-btn ${item.is_active ? 'active' : 'inactive'}`}
                            onClick={() => handleToggleActive(item)}
                            title={item.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                          >
                            {item.is_active ? <FaEye /> : <FaEyeSlash />}
                          </button>
                          <button
                            className="action-btn"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDelete(item.id)}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </Container>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg" className="gallery-modal-admin">
          <Modal.Header closeButton className="modal-header-admin">
            <Modal.Title>
              <FaPhotoVideo className="me-2" />
              {editingItem ? 'Edit Gallery Item' : 'Add Gallery Item'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter title for the gallery item"
                      className="form-control-custom"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category *</Form.Label>
                    <Form.Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      className="form-control-custom"
                    >
                      {Object.entries(categoryConfig).map(([key, category]) => (
                        <option key={key} value={key}>
                          <span style={{ color: category.color, marginRight: '8px' }}>
                            {React.cloneElement(category.icon, { style: { marginRight: '8px' } })}
                          </span>
                          {category.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleTextareaChange}
                  rows={3}
                  placeholder="Describe this gallery item..."
                  className="form-control-custom"
                />
              </Form.Group>
              
              <div className="mb-4">
                <Form.Label>Media Type *</Form.Label>
                <div className="btn-type-selector">
                  <Button
                    type="button"
                    variant={formData.gallery_type === 'photo' ? 'primary' : 'outline-primary'}
                    onClick={() => handleGalleryTypeChange('photo')}
                    className={formData.gallery_type === 'photo' ? 'active' : ''}
                  >
                    <FaImage className="me-2" /> Photo
                  </Button>
                  <Button
                    type="button"
                    variant={formData.gallery_type === 'video' ? 'primary' : 'outline-primary'}
                    onClick={() => handleGalleryTypeChange('video')}
                    className={formData.gallery_type === 'video' ? 'active' : ''}
                  >
                    <FaVideo className="me-2" /> Video
                  </Button>
                </div>
              </div>
              
              {formData.gallery_type === 'photo' ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Upload Image *</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                      className="form-control-custom"
                    />
                    <Form.Text className="text-muted">
                      Supported formats: JPG, PNG, WebP (Max 5MB)
                    </Form.Text>
                  </Form.Group>
                  
                  {/* Preview */}
                  <div className={`preview-container ${(imagePreview || formData.image_path) ? 'has-image' : ''}`}>
                    {imagePreview || formData.image_path ? (
                      <>
                        <img 
                          src={imagePreview || getImageUrl(formData.image_path)} 
                          alt="Preview" 
                          className="preview-image"
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={removeImage}
                          className="mt-2"
                        >
                          <FaTrash className="me-1" /> Remove Image
                        </Button>
                      </>
                    ) : (
                      <>
                        <FaUpload size={48} className="text-muted mb-3" />
                        <p className="text-muted">Image preview will appear here</p>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Video URL *</Form.Label>
                  <Form.Control
                    type="url"
                    name="video_url"
                    value={formData.video_url}
                    onChange={(e) => handleVideoUrlChange(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                    required
                    className="form-control-custom"
                  />
                  <Form.Text className="text-muted">
                    Supports YouTube and Vimeo URLs
                  </Form.Text>
                  
                  {formData.video_url && (
                    <div className="preview-container has-image mt-3">
                      <div className="ratio ratio-16x9">
                        <iframe
                          src={formData.video_url}
                          title="Video preview"
                          allowFullScreen
                          className="video-preview-frame"
                        ></iframe>
                      </div>
                    </div>
                  )}
                </Form.Group>
              )}
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  name="is_active"
                  label="Active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  id="active-switch"
                />
                <Form.Text className="text-muted">
                  Active items will be visible on the public website
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} disabled={uploadingImage}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingImage}>
                {uploadingImage && <Spinner animation="border" size="sm" className="me-2" />}
                {editingItem ? 'Update' : 'Add'} Gallery Item
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default GalleryAdmin;